import React, { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, Save, Loader2, Search, User } from "lucide-react";
import api from "../../services/api.js";

const ManualInvoiceModal = ({ isOpen, onClose, onSuccess }) => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    userid: "", // Biasanya ID Admin/User yang login
    cust_id: "",
    firstname: "",
    lastname: "",
    duedate: "",
    tax: 11,
    tax2: 0,
    credit: 0,
    notes: "",
    items: [{ description: "", amount: 0, taxed: 0, notes: "" }],
  });

  const [totals, setTotals] = useState({ subtotal: 0, total: 0 });

  // Handle klik di luar dropdown untuk menutup list pencarian
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounce Search Customer
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 2) {
        fetchCustomers(searchTerm);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Hitung total otomatis dengan logika PERSENTASE untuk Tax
  useEffect(() => {
    const subtotal = formData.items.reduce(
      (acc, item) => acc + (Number(item.amount) || 0),
      0
    );

    // Hitung nominal pajak dari persen
    const taxNominal = subtotal * (Number(formData.tax) / 100);
    const tax2Nominal = subtotal * (Number(formData.tax2) / 100);

    const total = subtotal + taxNominal + tax2Nominal - Number(formData.credit);

    setTotals({
      subtotal,
      taxNominal, // Kita simpan nominalnya untuk display jika perlu
      tax2Nominal,
      total,
    });
  }, [formData]);

  const fetchCustomers = async (query) => {
    setSearchLoading(true);
    try {
      const response = await api.get(`/customers?search=${query}&limit=5`, {
        headers: { Authorization: token },
      });
      setCustomers(response.data.data);
      setShowDropdown(true);
    } catch (error) {
      console.error("Gagal mengambil data customer", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const selectCustomer = (customer) => {
    // Split nama untuk firstname & lastname
    const names = customer.full_name.split(" ");
    const first = names[0];
    const last = names.slice(1).join(" ");

    setFormData({
      ...formData,
      userid: customer.id, // Masuk ke kolom userid (PK Table Customers)
      cust_id: customer.customer_id, // Masuk ke kolom cust_id (String ID)
      firstname: first,
      lastname: last,
    });

    setSearchTerm(customer.full_name);
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Siapkan payload dengan mengonversi persen ke nominal rupiah
    const finalPayload = {
      ...formData,
      tax: totals.taxNominal, // Kirim nominal ke backend
      tax2: totals.tax2Nominal, // Kirim nominal ke backend
    };

    try {
      await api.post(`/invoices-single`, finalPayload, {
        headers: { Authorization: token },
      });
      alert("Invoice berhasil dibuat!");
      onSuccess();
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || "Gagal menyimpan");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for items
  const handleAddItem = () =>
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { description: "", amount: 0, taxed: 0, notes: "" },
      ],
    });
  const handleRemoveItem = (index) =>
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-900 p-4 border-b dark:border-slate-800 flex justify-between items-center z-20">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              Create New Invoice
            </h2>
            <p className="text-xs text-slate-500">
              Buat tagihan baru untuk pelanggan
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 dark:text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Section 1: Customer Search & Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 dark:bg-slate-800/20 p-4 rounded-xl border dark:border-slate-800">
            {/* SEARCH CUSTOMER */}
            <div className="relative" ref={dropdownRef}>
              <label className="block text-sm font-semibold mb-1.5 dark:text-slate-300">
                Cari Pelanggan
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Ketik Nama atau Customer ID..."
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() =>
                    searchTerm.length >= 2 && setShowDropdown(true)
                  }
                />
                {searchLoading && (
                  <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-blue-500" />
                )}
              </div>

              {/* Dropdown Hasil Search */}
              {showDropdown && (
                <div className="absolute w-full mt-1 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-xl z-30 max-h-60 overflow-y-auto">
                  {customers.length > 0 ? (
                    customers.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => selectCustomer(c)}
                        className="p-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer border-b dark:border-slate-700 last:border-0"
                      >
                        <div className="font-bold text-sm dark:text-white">
                          {c.full_name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {c.customer_id} • {c.email || "No Email"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-sm text-slate-500">
                      Data tidak ditemukan
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* USER ID (Admin/Creator) */}
            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 dark:text-slate-400">
                Database User ID (Ref: customers.id)
              </label>
              <div className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-800/50 border dark:border-slate-800 rounded-lg text-slate-500 dark:text-slate-400 italic text-sm">
                {formData.userid
                  ? formData.userid
                  : "Otomatis terisi saat customer dipilih..."}
              </div>
            </div>

            {/* READONLY AUTO-FILL FIELDS */}
            <div className="grid grid-cols-2 gap-4 md:col-span-2">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-400 mb-1">
                  Customer ID
                </label>
                <input
                  type="text"
                  readOnly
                  className="w-full p-2 bg-slate-100 dark:bg-slate-900 border dark:border-slate-800 rounded dark:text-slate-400"
                  value={formData.cust_id}
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-slate-400 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  className="w-full p-2 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded dark:text-white"
                  onChange={(e) =>
                    setFormData({ ...formData, duedate: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          {/* Section 2: Items */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <div className="w-1.5 h-5 bg-blue-600 rounded-full"></div>
                Daftar Item / Layanan
              </h3>
              <button
                type="button"
                onClick={handleAddItem}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Tambah Item
              </button>
            </div>

            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div
                  key={index}
                  className="group relative flex flex-wrap md:flex-nowrap gap-3 p-4 bg-white dark:bg-slate-800/40 border dark:border-slate-800 rounded-xl"
                >
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Deskripsi item..."
                      required
                      className="w-full p-2 bg-transparent border-b dark:border-slate-700 dark:text-white outline-none focus:border-blue-500"
                      value={item.description}
                      onChange={(e) =>
                        handleItemChange(index, "description", e.target.value)
                      }
                    />
                  </div>
                  <div className="w-full md:w-40">
                    <div className="relative">
                      <span className="absolute left-0 top-2 text-slate-400 text-sm">
                        Rp
                      </span>
                      <input
                        type="number"
                        placeholder="0"
                        required
                        className="w-full p-2 pl-7 bg-transparent border-b dark:border-slate-700 dark:text-white outline-none focus:border-blue-500 text-right"
                        value={item.amount}
                        onChange={(e) =>
                          handleItemChange(index, "amount", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-400 hover:text-red-600 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t dark:border-slate-800">
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-slate-300">
                Catatan Internal
              </label>
              <textarea
                rows="4"
                placeholder="Tambahkan catatan jika ada..."
                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-xl dark:text-white outline-none"
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              ></textarea>
            </div>

            <div className="space-y-3 bg-blue-50/50 dark:bg-blue-900/10 p-5 rounded-2xl border border-blue-100 dark:border-blue-900/30">
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Subtotal</span>
                <span className="font-mono">
                  Rp {totals.subtotal.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-[10px] uppercase text-slate-500 font-bold flex justify-between">
                    Tax 1 (%) <span>Nominal</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="0"
                      className="w-full bg-transparent border-b border-slate-300 dark:border-slate-700 dark:text-white outline-none focus:border-blue-500 pr-6"
                      value={formData.tax}
                      onChange={(e) =>
                        setFormData({ ...formData, tax: e.target.value })
                      }
                    />
                    <span className="absolute right-0 top-0 text-slate-400 text-sm">
                      %
                    </span>
                  </div>
                  {/* Preview nominal rupiah di bawah input */}
                  <p className="text-[10px] text-blue-500 mt-1 italic">
                    + Rp {totals.taxNominal?.toLocaleString()}
                  </p>
                </div>
                <div className="flex-1">
                  <label className="text-[10px] uppercase text-slate-500 font-bold flex justify-between">
                    Credit (-)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="0"
                      className="w-full bg-transparent border-b border-slate-300 dark:border-slate-700 dark:text-white outline-none focus:border-blue-500 pr-6"
                      value={formData.credit}
                      onChange={(e) =>
                        setFormData({ ...formData, credit: e.target.value })
                      }
                    />
                    <span className="absolute right-0 top-0 text-slate-400 text-sm">
                      IDR
                    </span>
                  </div>
                  {/* Preview nominal rupiah di bawah input */}
                  <p className="text-[10px] text-blue-500 mt-1 italic">- Rp</p>
                </div>
              </div>
              <div className="pt-4 flex justify-between items-center">
                <span className="font-bold text-slate-800 dark:text-white">
                  Total Tagihan
                </span>
                <span className="text-2xl font-black text-blue-600 dark:text-blue-400 font-mono">
                  Rp {totals.total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all font-medium"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading || !formData.cust_id}
              className="px-8 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 transition-all"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              Terbitkan Invoice
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualInvoiceModal;
